<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import groupBy from "lodash/groupBy";
import SvgIcon from '../SvgIcon.vue';
import { paramIconMap } from './constants';
import { useUpdateQuery } from './composables';

const props = defineProps({
    name: {
        type: String,
        required: true,
    },
    selectedIndex: {
        type: [Number, null],
        default: null,
    },
});

defineEmits(['click']);

const store = useStore();

const show = ref(true);

class Area {
    constructor(params) {
        this.text = params.text;
        this.code = params.code;
        this.id = params.id;
        this.type = params.type;
    }
}


const featuredAreasByType = computed(() => {
    const featuredAreas = store.state.featuredAreas.map(featuredArea => new Area(featuredArea));
    return groupBy(featuredAreas, area => area.type);
});

function getFlatIndex(groupIndex, index) {
    return Object.values(featuredAreasByType.value).slice(0, groupIndex).flat().length + index;
}


// This usually comes from the useAutocompletions composable,
// but since this is not shown together with autocompletion, we can have an empty completionUpdateFunction
const requestCompletionsUpdate = () => {};
const {
    handleSelectSuggestion,
    handleRemoveChosenQueryParam,
    // note requestCompletionUpdate is just an empty function. See comment above.
} = useUpdateQuery(requestCompletionsUpdate, store);

/**
 * @param {{ id: string, duration: number }} featuredArea
 */
function createClickHandler(featuredArea) {
    return () => {
        const metaData = {
            type: 'geoareaid',
        };
        const suggestion = {
            value: featuredArea.id,
            text: featuredArea.text,
        };

        if (isInSearchQuery(metaData.type, featuredArea.id)) {
            handleRemoveChosenQueryParam(suggestion, metaData.type);
        } else {
            handleSelectSuggestion(suggestion, metaData);
        }
        return {
            emitEvent: {
                name: 'suggestionSelected',
                params: {},
            },
        };
    };
}

const clickHandlers = computed(() => Object.values(featuredAreasByType.value).flat().map(createClickHandler));

defineExpose({
    numSelectableItems: clickHandlers.value.length,
    clickHandlers,
    name: props.name,
    show,
    hasContent: Object.keys(featuredAreasByType.value),
});

function isInSearchQuery(type, featuredAreaId) {
    return store.getters.workingQuery[type]?.includes(featuredAreaId);
}

</script>

<template>
    <div v-show="show">
        <div
            class="jobsearch-dropdown-section"
            v-for="featuredAreas, type, groupIndex in featuredAreasByType"
            :key="type"
        >
            <div class="category-header jobsearch-dropdown-section__header">
                {{ type }}
            </div>
            <ul>
                <li
                    class="jobsearch-dropdown-section__suggestion d-flex justify-content-between"
                    v-for="featuredArea, index in featuredAreas"
                    :key="featuredArea.type + featuredArea.id"
                    :class="{ 'jobsearch-dropdown-section__suggestion--selected': selectedIndex === getFlatIndex(groupIndex, index) }"
                    @click="$emit('click', getFlatIndex(groupIndex, index))"
                >
                    <div>
                        <SvgIcon
                            :name="paramIconMap.geoareaid"
                            size="xs"
                            role="presentation"
                            class="mr-2"
                        />
                        <span :class="{'font-weight-bold': isInSearchQuery('geoareaid', featuredArea.id)}">
                            {{ featuredArea.text }}
                        </span>
                    </div>
                    <SvgIcon
                        size="mini"
                        class="search-parameter-selected-icon"
                        name="button-check"
                        v-if="isInSearchQuery('geoareaid', featuredArea.id)"
                    />
                </li>
            </ul>
        </div>
    </div>
</template>
